import { useState, useEffect } from 'react';
import { plansApi, membershipsApi } from '@app/api/organization';
import { authApi } from '@app/api/auth';
import { logger } from '@app/utils/logger';
import type { ServicePlan, User } from '@app/types';

export const useRosterData = (churchId: string | null | undefined, groupId?: string | null) => {
  const [allPlans, setAllPlans] = useState<ServicePlan[]>([]);
  const [availableMembers, setAvailableMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [orphanedPlanCount, setOrphanedPlanCount] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!churchId) return;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadError(false);
        const [plans, membersData] = await Promise.all([
          plansApi.getRecent(churchId, 100, groupId ?? undefined),
          membershipsApi.getChurchMembers(churchId),
        ]);

        setAllPlans(plans);

        // Detect orphaned plans (null groupId) — only check once with no group filter
        if (!groupId) {
          const orphaned = plans.filter(p => !p.groupId);
          setOrphanedPlanCount(orphaned.length);
        }

        const userIds = Array.from(new Set(membersData.map(m => m.userId)));
        const users = await authApi.batchGetUsers(userIds);
        setAvailableMembers(users);
      } catch (error) {
        logger.error('Failed to load roster data:', error);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [churchId, groupId, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  return { allPlans, setAllPlans, availableMembers, loading, loadError, reload, orphanedPlanCount, setOrphanedPlanCount };
};
